import { useState } from 'react';
import { User } from '@phosphor-icons/react/dist/ssr/User';
import { CaretLeft } from '@phosphor-icons/react/dist/ssr/CaretLeft';
import IconBack from '../Icons/IconBack';
import { BORDER_RADIUS, BUTTON_TOKENS } from '../../../styles/UISystem';
import { Gift } from '@phosphor-icons/react/dist/ssr/Gift';
import StatusSubtitle, { ActivityDot } from '../StatusSubtitle';

/**
 * HeaderConnectionImage - Compact inline header for connection pages
 *
 * Single row layout:
 *   Left: back caret (light grey) | gap | photo (40px) + peer first name + status subtitle
 *   Right: Invite button or empty
 *
 * Back caret and photo+name are separate tap targets.
 *
 * Anon-side variant (exchange-polish, 2026-07-21): `dotOnly` drops ALL status
 * text — a green dot on the name line when the peer is active now, otherwise
 * nothing (the Profile gets the status language; the Anon gets simplicity).
 * `doorChevron` renders "{Name} ›" — the visible door to the detail page
 * (the photo+name group has always carried the tap; the chevron says so).
 *
 * Pre-name (Profile side): the name line collapses when there is no name and
 * no placeholder, so the StatusSubtitle ("Active now" from the heartbeat)
 * sits alone beside the photo — the mock's name-less state.
 */
// The chat message list's left inset (ConnectionPageShared). The header's
// content rule matches it so the peer and the posts share one edge.
const CHAT_LEFT_RULE = 16;
// Clear air between the back chevron and the peer's avatar. They are two
// different actions — leave, and open the person — so they must not read as
// one cluster (WhatsApp and Telegram both hold this gap; user, 2026-08-16).
const BACK_TO_PEER_GAP = 14;

const HeaderConnectionImage = ({
  name = '',
  imageUrl = '',
  showBackButton = true,
  onBackClick,
  onImageClick,
  showPlaceholder = true,
  namePlaceholder = 'Name',
  showPhoto = true,
  activityStatus = null,
  locationDisplay = null,
  showInviteButton = false,
  onInviteToRoyale = null,
  inviteSent = false,
  dotOnly = false,
  doorChevron = false,
  alignToChatRule = false,
  // Rendered immediately right of the back chevron, inside the back group —
  // THREAD's connection-count badge (iMessage's back-to-list shape).
  backBadge = null,
}) => {
  // Prefetched image trust — skip load event if prefetch provided the same URL
  const hasPrefetchedData =
    typeof window !== 'undefined' && window.__EXCHANGE_DATA__?.serverRendered;
  const prefetchedPhoto = window.__EXCHANGE_DATA__?.profilePhoto;

  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const prefetchPhotoMatches =
    hasPrefetchedData && prefetchedPhoto && imageUrl && prefetchedPhoto === imageUrl;
  const showImage = imageLoaded || prefetchPhotoMatches;

  // Whether to render the photo circle
  const hasPhoto = showPhoto && (imageUrl || showPlaceholder);

  // Display first name only
  const displayName = name && name.trim() ? name.trim().split(' ')[0] : '';

  // Photo + name + status — one tap target to the detail page.
  const photoAndName = (
    <div
      onClick={onImageClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: alignToChatRule ? '8px' : '10px',
        cursor: onImageClick ? 'pointer' : 'default',
        minWidth: 0,
      }}
    >
      {hasPhoto && (
        <div style={{ flexShrink: 0 }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#E5E5E5',
              overflow: 'hidden',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {imageUrl && !imageError ? (
              <>
                <img
                  src={imageUrl}
                  alt="Profile"
                  onLoad={() => setImageLoaded(true)}
                  onError={() => setImageError(true)}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: showImage ? 'block' : 'none',
                  }}
                />
                {!showImage && <User size={20} color="#8E8E93" weight="regular" />}
              </>
            ) : showPlaceholder ? (
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#C7C7CC',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <User size={20} color="#FFFFFF" weight="regular" />
              </div>
            ) : null}
          </div>
        </div>
      )}

      <div
        style={{
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        {(displayName || (showPlaceholder && namePlaceholder)) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
            <span
              style={{
                fontSize: '20px',
                fontWeight: '700',
                color: displayName ? '#000000' : '#8E8E93',
                letterSpacing: '-0.3px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {displayName || namePlaceholder}
            </span>

            {dotOnly && activityStatus?.isActive && <ActivityDot />}

            {doorChevron && (
              <span
                style={{
                  fontSize: '20px',
                  fontWeight: '400',
                  color: '#C7C7CC',
                  flexShrink: 0,
                  lineHeight: 1,
                }}
              >
                ›
              </span>
            )}
          </div>
        )}

        {!dotOnly && (
          <StatusSubtitle
            locationDisplay={locationDisplay}
            activityStatus={activityStatus}
            size={12}
          />
        )}
      </div>
    </div>
  );

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: '4px',
        paddingBottom: '6px',
        // THREAD aligns the header to the SAME left rule as the chat content
        // (the message list's 16px inset), so with no back arrow the peer and
        // the posts share one edge.
        paddingLeft: alignToChatRule ? `${CHAT_LEFT_RULE}px` : '4px',
        paddingRight: '16px',
        minHeight: '52px',
      }}
    >
      {/* Left group: back caret (separate target) + photo + peer name */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          minWidth: 0,
          flex: 1,
        }}
      >
        {/* Back button. THREAD uses a COMPACT chevron (IconBack's 44px box
            cannot fit left of a 16px rule without running off-screen), pulled
            a little closer to the screen edge and then given REAL SPACE before
            the avatar.

            The space is the point, not a nicety: back and the avatar do
            completely different things — one leaves, one opens the person —
            and WhatsApp and Telegram both separate them clearly. An earlier cut
            hung the chevron inside the 16px margin so the avatar could line up
            with the posts; that alignment is worth less than two adjacent
            controls reading as two controls. With no back arrow the avatar
            still sits on the rule. */}
        {showBackButton &&
          (alignToChatRule ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
                marginLeft: '-8px',
                marginRight: `${BACK_TO_PEER_GAP}px`,
              }}
            >
              <div
                onClick={onBackClick}
                style={{
                  display: 'flex',
                  padding: '11px',
                  margin: '-11px',
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <CaretLeft size={20} color={BUTTON_TOKENS.BACK_ICON} weight="bold" />
              </div>
              {backBadge}
            </div>
          ) : (
            <div style={{ marginRight: '8px', flexShrink: 0 }}>
              <IconBack onClick={onBackClick} variant="plain" />
            </div>
          ))}

        {photoAndName}
      </div>

      {/* Right group: Invite button (Profile viewing Anon) */}
      {showInviteButton && onInviteToRoyale && !inviteSent && (
        <div style={{ flexShrink: 0, marginLeft: '12px' }}>
          <button
            onClick={onInviteToRoyale}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: BORDER_RADIUS.PILL,
              backgroundColor: '#1C1C1E',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <Gift size={16} color="#FFFFFF" weight="light" />
            <span
              style={{
                fontSize: '15px',
                fontWeight: '500',
                color: '#FFFFFF',
                whiteSpace: 'nowrap',
              }}
            >
              Invite
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default HeaderConnectionImage;
